import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, MapPin, Car, Users, Heart, Gift, HelpCircle, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';

export default function ImNewPage() {
  const { t } = useTranslation();

  const expectItems = [
    { icon: Users, title: t('imNew.arrival'), desc: t('imNew.arrivalDesc'), color: 'bg-burgundy' },
    { icon: Heart, title: t('imNew.worship'), desc: t('imNew.worshipDesc'), color: 'bg-gold' },
    { icon: Gift, title: t('imNew.dress'), desc: t('imNew.dressDesc'), color: 'bg-purple' },
    { icon: Car, title: t('imNew.parking'), desc: t('imNew.parkingDesc'), color: 'bg-olive' },
    { icon: Users, title: t('imNew.kids'), desc: t('imNew.kidsDesc'), color: 'bg-burgundy' },
  ];

  return (
    <div className="min-h-screen bg-cream pt-24" data-testid="im-new-page">
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('imNew.planVisit')}</span>
            <h1 className="font-serif text-4xl md:text-6xl text-charcoal mb-6">
              {t('imNew.title')}
            </h1>
            <p className="text-charcoal/70 font-sans text-lg max-w-3xl mx-auto leading-relaxed">
              {t('imNew.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Service Times Banner */}
      <section className="py-8 bg-burgundy">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-white">
            <div className="text-center">
              <span className="text-gold text-xs uppercase tracking-widest block mb-2">{t('imNew.serviceTimes')}</span>
              <div className="flex items-center gap-2">
                <Clock size={20} className="text-gold" />
                <span className="text-lg">{t('contact.sundayWorship')}</span>
              </div>
            </div>
            <div className="hidden md:block w-px h-12 bg-white/20" />
            <div className="flex items-center gap-2">
              <MapPin size={20} className="text-gold" />
              <span>109 N Beaumont Avenue #1078, Kissimmee, FL 34741</span>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('imNew.whatToExpect')}</span>
            <h2 className="font-serif text-3xl md:text-4xl text-charcoal mb-4">{t('imNew.expectIntro')}</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {expectItems.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl border border-gold/10 hover:border-gold/30 transition-all duration-300"
                data-testid={`expect-item-${i}`}
              >
                <div className={`${item.color} w-12 h-12 rounded-xl flex items-center justify-center mb-4`}>
                  <item.icon size={24} className="text-white" />
                </div>
                <h3 className="font-serif text-lg text-charcoal mb-2">{item.title}</h3>
                <p className="text-charcoal/60 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* First Time Guest Banner */}
      <section className="py-12 bg-gold/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Gift size={48} className="text-gold mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-charcoal mb-2">{t('imNew.firstTimeGuest')}</h3>
            <p className="text-charcoal/70">{t('imNew.firstTimeGuestDesc')}</p>
          </motion.div>
        </div>
      </section>

      {/* Questions CTA */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <HelpCircle size={48} className="text-burgundy mx-auto mb-4" />
            <h2 className="font-serif text-3xl text-charcoal mb-4">{t('imNew.questions')}</h2>
            <p className="text-charcoal/70 mb-8">{t('imNew.questionsDesc')}</p>
            <Link to="/contact">
              <Button className="bg-burgundy hover:bg-purple text-white px-8 py-4">
                {t('imNew.contactUs')} <ChevronRight size={18} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Google Maps Embed */}
      <section className="py-8" data-testid="map-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3510.4876!2d-81.4097!3d28.2984!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88dd810c3a7d0f3b%3A0x4a5f41c8f8d3d3d3!2s109%20N%20Beaumont%20Ave%2C%20Kissimmee%2C%20FL%2034741!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Living Fruit Ministry Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
